/* global CCPAEnabled, FB */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { browserHistory } from 'react-router';
import { connect } from 'react-redux';
import _ from 'lodash';
import {
  DEFAULT_CHANGE_PASSWORD_STATE,
  DEFAULT_CREATE_PASSWORD_STATE,
  DEFAULT_FACEBOOK_PASSWORD_LINKING_STATE,
  DEFAULT_LINK_ACCOUNT_STATE,
  DEFAULT_OVERLAY_STATE,
  DEFAULT_PARENTAL_CONTROLS_STATE,
  DEFAULT_SIGNIN_STATE,
  DEFAULT_SIGNUP_STATE,
  DEFAULT_STATE,
  isValidEmail,
  isValidPassword,
  passwordErrors,
} from 'helpers/Account';
import {
  getActivateCodeFromURL,
  getActivateCode,
  redirectToActivateMVPD,
  setActivateCode,
  setActivateCodeAccount,
} from 'helpers/ActivateHelper';
import { getCommonTealiumProps } from 'helpers/AccountHelper';
import { login as facebookLogin } from 'helpers/Facebook';
import { CCPA_VERIFY_COOKIE_NAME, getUrlParameter, v1Override } from 'helpers/ContentHelper';
import {
  helperGetHomeUrl,
  helperGoToRelativeUrl,
  redirectToExternalUrl,
  getPreviousSettingPath,
} from 'helpers/RouteHelper';
import { secondScreenProfileEnabled } from 'helpers/ConfigHelper';
import { noop } from 'helpers/GlobalHelper';
import {
  Events,
  handleAnalytics,
  mountSessionCam,
  SegmentPageProfileTypes,
} from '@foxcorp/delta-web-analytics';
import { logEvent } from 'helpers/LogReportHelpers';
import { setCookie } from 'helpers/Storage';
import { setIsGoogleAutoSignIn } from 'helpers/AuthHelper';
import {
  onEditAccountBackClick,
  renderAppleLinking,
  renderChangePassword,
  renderCreatePassword,
  renderEditAccount,
  renderFacebookLinking,
  renderParentalControls,
  setEditAccountErrors,
  setFacebookLinkingErrors,
} from './Overlays/index';
import {
  ActivationSuccess,
  AccountMain,
  ConsolidatedSignInFlow,
  DeleteAccount,
  ForgotPassword,
  ForgotPasswordSuccess,
  Logout,
  ManageFavorites,
  ResetPassword,
  ResetPasswordSuccess,
  ShareMyEmailUpsell,
  Signin,
  Signup,
  SignupSuccess,
  SocialFacebookPasswordLink,
  VerifyAccount,
} from './AccountComponents/index';

import { setGoogleOneTap } from '../../index/AppActions';
import Style from './SharedStyles/SharedStyles.module.scss';

const SCREENS = {
  resetPassword: 'showResetPassword',
  signup: 'showSignup',
  main: 'showAccountMain',
};
const NAME = 'Account';
const EMAIL_INVALID = '400';
const EMAIL_IS_REGISTERED = '409';
const EMAIL_IS_REGISTERED_TO_SOCIAL = '4090001';
const SKIP = 'skip';

class Account extends Component {
  constructor(props) {
    super(props);
    const { initialScreen, renderNetworkLink } = this.props;

    this.screen = _.get(SCREENS, initialScreen, null);
    this.fbAuthStarted = false;
    this.didComponentMount = false;

    const isVerifyScreen = window.location.pathname.includes('ccpa/verify');
    // Set Initial screen to true (initialScreen Ex: showResetPassword or showSignUp)
    // without modifying DEFAULT_STATE so it can be reset at later stages
    // (Ex: When receiving new props)
    let DEFAULTS = DEFAULT_STATE;
    if (this.screen) {
      DEFAULTS = _.clone(DEFAULT_STATE);
      DEFAULTS[this.screen] = true;
    }
    this.state = {
      ...DEFAULTS,
      signInEventFired: false,
      signUpEventFired: false,
      showShareMyEmailUpsell: false,
      showVerifyAccount: isVerifyScreen,
      fromVerifyAccount: false,
      showVerifyConfirmation: false,
      ccpaRequestId: null,
    };

    // overlays
    this.renderAppleLinking = renderAppleLinking.bind(this);
    this.renderChangePassword = renderChangePassword.bind(this);
    this.renderCreatePassword = renderCreatePassword.bind(this);
    this.renderEditAccount = renderEditAccount.bind(this);
    this.renderFacebookLinking = renderFacebookLinking.bind(this);
    this.renderSocialFacebookPasswordLink = this.renderSocialFacebookPasswordLink.bind(this);
    this.renderParentalControls = renderParentalControls.bind(this);

    // render helpers
    this.renderLogo = renderNetworkLink;

    // setters
    this.toggleEditAccount = this.toggleEditAccount.bind(this);
    this.setEditAccountErrors = setEditAccountErrors.bind(this);
    this.toggleResetPassword = this.toggleResetPassword.bind(this);
    this.toggleResetPasswordSuccess = this.toggleResetPasswordSuccess.bind(this);
    this.toggleManageFavorites = this.toggleManageFavorites.bind(this);
    this.toggleShowPerks = this.toggleShowPerks.bind(this);
    this.toggleSignup = this.toggleSignup.bind(this);
    this.toggleSignupSuccess = this.toggleSignupSuccess.bind(this);
    this.toggleSignin = this.toggleSignin.bind(this);
    this.toggleSignOut = this.toggleSignOut.bind(this);
    this.toggleAccountMain = this.toggleAccountMain.bind(this);
    this.toggleForgotPassword = this.toggleForgotPassword.bind(this);
    this.toggleChangePassword = this.toggleChangePassword.bind(this);
    this.toggleCreatePassword = this.toggleCreatePassword.bind(this);
    this.toggleAppleLinkAccount = this.toggleAppleLinkAccount(this);
    this.setSigninErrors = this.setSigninErrors.bind(this);
    this.setSignupErrors = this.setSignupErrors.bind(this);
    this.resetSignupState = this.resetSignupState.bind(this);
    this.toggleFacebookLinking = this.toggleFacebookLinking.bind(this);
    this.setFacebookLinkingErrors = setFacebookLinkingErrors.bind(this);
    this.setLinkingEmail = this.setLinkingEmail.bind(this);
    this.setAppleToken = this.setAppleToken.bind(this);
    this.setAppleLinkingEmail = this.setAppleLinkingEmail.bind(this);
    this.setIsAppleEmailShared = this.setIsAppleEmailShared.bind(this);
    this.setLinkingToken = this.setLinkingToken.bind(this);
    this.toggleFacebookPasswordLink = this.toggleFacebookPasswordLink.bind(this);
    this.toggleFacebookLoginPasswordLink = this.toggleFacebookLoginPasswordLink.bind(this);
    this.toggleParentalControls = this.toggleParentalControls.bind(this);

    // event handlers
    this.onEditAccountBackClick = onEditAccountBackClick.bind(this);
    this.onAppleSignIn = this.onAppleSignIn.bind(this);
    this.onAppleLinking = this.onAppleLinking.bind(this);
    this.onRegisterWithGoogle = this.onRegisterWithGoogle.bind(this);
    this.onFieldChange = this.onFieldChange.bind(this);
    this.onSignupBackClick = this.onSignupBackClick.bind(this);
    this.onSignup = this.onSignup.bind(this);
    this.onSigninBackClick = this.onSigninBackClick.bind(this);
    this.onSignin = this.onSignin.bind(this);
    this.onSigninRegisterClick = this.onSigninRegisterClick.bind(this);
    this.onForgotPasswordClick = this.onForgotPasswordClick.bind(this);
    this.onSignupLoginClick = this.onSignupLoginClick.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onLogout = this.onLogout.bind(this);
    this.onDeleteAccount = this.onDeleteAccount.bind(this);
    this.onFavoritesBackClick = this.onFavoritesBackClick.bind(this);
    this.onForgotPassword = this.onForgotPassword.bind(this);
    this.onResetPassword = this.onResetPassword.bind(this);
    this.onSocialFacebookLogin = this.onSocialFacebookLogin.bind(this);
    this.onSocialFacebookRegister = this.onSocialFacebookRegister.bind(this);
    this.onLinkFacebook = this.onLinkFacebook.bind(this);
    this.onShowFacebookPasswordLink = this.onShowFacebookPasswordLink.bind(this);
  }

  componentDidMount() {
    const { account } = this.props;

    if (CCPAEnabled) {
      const ccpaRequestId = getUrlParameter('requestId') || getUrlParameter('requestid');
      this.setState({ ccpaRequestId });
    }

    if (account) this.handleActivateCode({ ...account, isNew: false });

    this.didComponentMount = true;

    return null;
  }

  // eslint-disable-next-line no-unused-react-component-methods/no-unused-react-component-methods
  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      account: accountNext,
      isMobile,
      isTablet,
      params: { overlay },
      showAccount: nextShowAccount,
      showForgotPasswordLanding: showForgotPasswordLandingNext,
      showSignIn: showSignInNext,
      showSignUp: showSignUpNext,
    } = nextProps;
    const {
      googleOneTap: { isGoogleOneTap },
      showAccount,
    } = this.props;
    const {
      showSignup,
      showSignin,
      showSignupSuccess,
      showResetPassword,
      showForgotPasswordLanding,
      showForgotPasswordSuccess,
      showManageFavorites,
      showChangePassword,
      showCreatePassword,
      showEditAccount,
      showLinkAccount,
      showSignOut,
      showParentalControls,
    } = this.state;
    const isWeb = !isMobile && !isTablet;

    if (this.handleActivateCode(accountNext)) return null;
    if (!showAccount && nextShowAccount) {
      if (!accountNext) {
        switch (overlay) {
          case 'sign-in':
            this.toggleSignin();
            break;
          case 'sign-up':
            this.toggleSignup();
            break;
          default:
        }
        return isGoogleOneTap
          ? this.toggleCreatePassword()
          : this.setState({ ...DEFAULT_STATE, showPerks: true });
      }
      if (!isWeb) return this.toggleAccountMain();
      switch (overlay) {
        case 'edit-profile':
          this.toggleEditAccount();
          break;
        case 'change-password':
          this.toggleChangePassword();
          break;
        case 'create-password':
          this.toggleCreatePassword();
          break;
        case 'link-account':
          this.toggleAppleLinkAccount();
          break;
        case 'manage-favorites':
          this.toggleManageFavorites();
          break;
        case 'sign-out':
          this.toggleSignOut();
          break;
        case 'parental-controls':
          this.toggleParentalControls();
          break;
        default:
          helperGoToRelativeUrl(helperGetHomeUrl());
          break;
      }
    }

    if (showForgotPasswordLandingNext && !showForgotPasswordLanding) {
      return this.setState({
        ...DEFAULT_STATE,
        showForgotPasswordLanding: true,
        onCloseResetToBasePath: true,
      });
    }

    if (showResetPassword) {
      return this.setState({
        ...DEFAULT_STATE,
        showResetPassword: true,
      });
    }

    if (showSignInNext && !showSignin) {
      return this.setState({
        ...DEFAULT_STATE,
        showSignin: true,
      });
    }

    if (showSignUpNext && !showSignup) {
      return this.setState({
        ...DEFAULT_STATE,
        showSignup: true,
      });
    }

    // dont update account overlay state if user is in the middle of the signup process
    if (!isWeb && accountNext) {
      const shouldNotUpdate =
        showSignup ||
        showSignupSuccess ||
        showManageFavorites ||
        showChangePassword ||
        showCreatePassword ||
        showEditAccount ||
        showLinkAccount ||
        showSignOut ||
        showForgotPasswordLanding ||
        showForgotPasswordSuccess ||
        showParentalControls;

      if (shouldNotUpdate) return null;

      return this.setState({
        ...DEFAULT_STATE,
        showAccountMain: true,
      });
    }
    return null;
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      isGoogleOneTapDisplayed,
      onboardingStart,
      perksPresented,
      showAccount,
      signInError,
      signUpError,
      utagAccountSource,
    } = this.props;
    const { showAccount: prevShowAccount } = prevProps;
    const {
      parentalControlsPasswordVerified,
      showParentalControls,
      showPerks,
      signinEmailError,
      signinGenericError,
      signinPasswordError,
      signupEmailError,
      signupEmailExistsError,
      signupGenericError,
      signupPasswordError,
    } = this.state;
    const {
      signinPasswordError: prevSigninPasswordError,
      signinEmailError: prevSigninEmailError,
      signinGenericError: prevSigninGenericError,
      signupGenericError: prevSignupGenericError,
      signupEmailError: prevSignupEmailError,
      signupEmailExistsError: prevSignupEmailExistsError,
      signupPasswordError: prevSignupPasswordError,
      parentalControlsPasswordVerified: prevParentalControlsPasswordVerified,
    } = prevState;
    const isSigninError =
      signInError &&
      ((signinPasswordError && !prevSigninPasswordError) ||
        (signinEmailError && !prevSigninEmailError) ||
        (signinGenericError && !prevSigninGenericError));
    const isSignupError =
      signUpError &&
      ((signupGenericError && !prevSignupGenericError) ||
        (signupEmailError && !prevSignupEmailError) ||
        (signupEmailExistsError && !prevSignupEmailExistsError) ||
        (signupPasswordError && !prevSignupPasswordError));
    const perksShown = !prevShowAccount && showAccount && showPerks && perksPresented;

    if (showPerks && isGoogleOneTapDisplayed) {
      window?.google?.accounts?.id?.cancel();
    }

    if (perksShown) {
      onboardingStart({ utagAccountSource });
      perksPresented({ utagAccountSource });
    }
    if (isSigninError) signInError({ utagAccountSource });
    if (isSignupError) signUpError({ utagAccountSource });

    const pcsPasswordVerified =
      showParentalControls &&
      parentalControlsPasswordVerified &&
      !prevParentalControlsPasswordVerified;
    if (pcsPasswordVerified) setTimeout(() => this.forceUpdate(), 1);
  }

  handleActivateCode = account => {
    if (!account || !account.accessToken) return null;
    const { signInEventFired } = this.state;
    if (!secondScreenProfileEnabled()) return null;
    if (signInEventFired) return null;

    const code = getActivateCodeFromURL() || getActivateCode();
    if (!code) return null;

    setActivateCode(code);
    if (window) _.set(window, 'secondScreenFlow', true);

    setActivateCodeAccount(account);
    const event = account.isNew ? Events.PROFILE_SIGNUP_COMPLETED : Events.PROFILE_SIGNIN_COMPLETED;
    const completeCB = () => redirectToActivateMVPD(code);
    handleAnalytics({
      event,
      payload: {
        account,
        page_start_source: 'general',
        page_profile_type: this.fbAuthStarted
          ? SegmentPageProfileTypes.FACEBOOK
          : SegmentPageProfileTypes.GENERAL,
        page_profile_activation_method: code && '2nd screen',
        page_activation_code: code,
      },
      tealiumPayload: {
        ...getCommonTealiumProps(),
      },
      callback: completeCB,
    });
    this.setState({ signInEventFired: true });
    return true;
  };

  handleShowShareMyEmailUpsell = showShareMyEmailUpsell => {
    this.setState({ showShareMyEmailUpsell });
  };

  toggleChangePassword() {
    const { showChangePassword } = this.state;
    const stateObj = !showChangePassword ? DEFAULT_CHANGE_PASSWORD_STATE : {};
    if (!showChangePassword)
      handleAnalytics({
        event: Events.ACCOUNT_UPDATE_PASSWORD_LANDING,
        payload: {
          pageName: 'account:update password',
          pageType: 'account',
        },
      });
    this.setState({ showChangePassword: !showChangePassword, ...stateObj });
  }

  toggleCreatePassword() {
    const { showCreatePassword } = this.state;
    const { createPasswordPageEvent } = this.props;
    const stateObj = !showCreatePassword ? DEFAULT_CREATE_PASSWORD_STATE : {};
    if (!showCreatePassword) createPasswordPageEvent();
    this.setState({ showCreatePassword: !showCreatePassword, ...stateObj });
  }

  toggleAppleLinkAccount() {
    const { showLinkAccount } = this.state;
    const stateObj = !showLinkAccount ? DEFAULT_LINK_ACCOUNT_STATE : {};
    if (this.didComponentMount) {
      this.setState({ showLinkAccount: !showLinkAccount, ...stateObj });
    }
  }

  toggleDeleteAccount() {
    const { showDeleteAccount } = this.state;
    this.setState({ showDeleteAccount: !showDeleteAccount });
  }

  toggleEditAccount() {
    const { showEditAccount } = this.state;
    const { account } = this.props;
    const stateObj = !showEditAccount
      ? {
          editFirstName: account.firstName || '',
          editLastName: account.lastName || '',
          editEmail: account.email || '',
          editGenericError: null,
          firstNameError: null,
          lastNameError: null,
        }
      : {};
    this.setState({
      showEditAccount: !showEditAccount,
      ...stateObj,
    });
  }

  toggleFacebookLinking() {
    const { showFacebookLinking } = this.state;
    this.setState(
      { showSignup: false, showSignin: false, showFacebookLinking: !showFacebookLinking },
      () => {
        const { showFacebookLinking } = this.state;
        if (showFacebookLinking) handleAnalytics({ event: Events.FB_SIGN_UP_STARTED });
      },
    );
  }

  toggleFacebookPasswordLink() {
    const { showFacebookPasswordLink } = this.state;
    this.setState({ showFacebookPasswordLink: !showFacebookPasswordLink }, () => {
      const { showFacebookPasswordLink } = this.state;
      if (showFacebookPasswordLink) handleAnalytics({ event: Events.FB_PASSWORD_CREATION_STARTED });
    });
  }

  toggleFacebookLoginPasswordLink() {
    const { showFacebookLoginPasswordLink } = this.state;
    this.setState({ showFacebookLoginPasswordLink: !showFacebookLoginPasswordLink });
  }

  toggleForgotPassword() {
    const { showForgotPassword, showForgotPasswordLanding } = this.state;
    this.resetAll(() => {
      this.setState({
        showForgotPassword: !showForgotPassword,
        showForgotPasswordLanding: !showForgotPasswordLanding,
      });
    });
  }

  toggleForgetPasswordSuccess() {
    const { showForgotPasswordSuccess } = this.state;
    this.setState({ showForgotPasswordSuccess: !showForgotPasswordSuccess });
  }

  toggleManageFavorites() {
    const { showManageFavorites } = this.state;
    this.setState({ showManageFavorites: !showManageFavorites });
  }

  toggleParentalControls() {
    const { showParentalControls } = this.state;
    const stateObj = !showParentalControls ? DEFAULT_PARENTAL_CONTROLS_STATE : {};
    this.setState({ showParentalControls: !showParentalControls, ...stateObj });
  }

  toggleResetPassword() {
    const { showResetPassword } = this.state;
    this.resetAll(() => {
      this.setState({ showResetPassword: !showResetPassword });
    });
  }

  toggleResetPasswordSuccess() {
    const { showResetPasswordSuccess } = this.state;
    this.setState({ showResetPasswordSuccess: !showResetPasswordSuccess });
  }

  toggleShowPerks() {
    const { showPerks } = this.state;
    this.setState({ showPerks: !showPerks });
  }

  toggleSignup() {
    const { showSignup } = this.state;
    this.resetSignupState(() => {
      this.setState({ showSignup: !showSignup });
    });
  }

  toggleSignupSuccess() {
    const { showSignupSuccess } = this.state;
    this.setState({ showSignupSuccess: !showSignupSuccess });
  }

  toggleSignin() {
    const { showSignin } = this.state;
    this.resetSigninState(() => {
      this.setState({ showSignin: !showSignin });
    });
  }

  toggleSignOut() {
    const { utagAccountSource } = this.props;
    const { showSignOut } = this.state;
    this.setState({ showSignOut: !showSignOut }, () => {
      handleAnalytics({
        event: Events.PROFILE_SIGN_OUT_STARTED,
        payload: { utagAccountSource },
      });
    });
  }

  async onLogout() {
    const { logout, previousPath, account } = this.props;
    if (account?.userType === 'google') {
      setIsGoogleAutoSignIn(false);
    }

    this.setState({
      logoutInProcess: true,
    });

    const logoutTimeout = setTimeout(() => {
      this.setState({
        logoutTimedOut: true,
      });
    }, 5000);

    const reset = () => {
      clearTimeout(logoutTimeout);
      this.setState({
        logoutTimedOut: false,
      });
      window.location = previousPath;
    };

    await logout();
    handleAnalytics({
      event: Events.PROFILE_SIGN_OUT,
      callback: reset,
    });
  }

  onSigninRegisterClick() {
    this.toggleSignin();
    this.toggleSignup();
  }

  onSignupLoginClick() {
    this.toggleSignup();
    this.toggleSignin();
  }

  onSignupBackClick() {
    this.toggleSignup();
    this.toggleShowPerks();
  }

  onSigninBackClick() {
    this.toggleSignin();
    this.toggleShowPerks();
  }

  onFavoritesBackClick() {
    // const { getFavorites } = this.props;
    this.toggleManageFavorites();
    this.toggleAccountMain();
    // if (getFavorites) getFavorites();
  }

  onClose(options) {
    const {
      closeAccount,
      resetAccountState,
      account,
      ignoreOnCloseRedirect,
      setAccountCloseIgnoreRedirect,
      setGoogleOneTap,
    } = this.props;
    const { goHome, ignoreRedirect, isCCPAVerify, ignoreSavedPath } = options || {};
    const isVerifyScreen = window.location.pathname.includes('ccpa/verify');
    if (isCCPAVerify) {
      setCookie({ name: CCPA_VERIFY_COOKIE_NAME, value: true, options: { path: '/' } });
    }
    const { onCloseResetToBasePath } = this.state;
    if (onCloseResetToBasePath) browserHistory.push('/');
    if (isVerifyScreen) redirectToExternalUrl(helperGetHomeUrl());
    this.handleShowShareMyEmailUpsell(false);
    const shouldIgnoreRedirect = ignoreRedirect || ignoreOnCloseRedirect;
    setAccountCloseIgnoreRedirect(false);
    resetAccountState();
    if (!account) {
      setGoogleOneTap({ isGoogleOneTap: false });
    }
    const nextState = {
      ...DEFAULT_OVERLAY_STATE,
      appleLinkingError: '',
      appleToken: '',
      createPasswordNewPassword: '',
      googleLinkingError: '',
      showVerifyAccount: false,
      showCreatePassword: false,
      showLinkAccount: false,
      showLinkToProfile: false,
      appleLinkingEmail: '',
    };
    if (account) nextState.showAccountMain = true;
    const savedPath = !ignoreSavedPath && getPreviousSettingPath();
    return this.setState(nextState, () =>
      closeAccount({ goHome, ignoreRedirect: shouldIgnoreRedirect, savedPath }),
    );
  }

  onSignup() {
    const { entryOverlay } = this.state;
    const {
      register,
      signUpCompleted,
      signUpMarketing,
      utagAccountSource,
      language: { profileCreate_invalidEmailText: invalidEmailError },
      anonymousAccount,
      signupSuccess,
    } = this.props;

    const { signupFirstName, signupLastName, signupEmail, signupPassword } = this.state;
    this.setState({ signUpEventFired: true });
    const handleSuccess = newAccount => {
      const newsLetterVal = 'Yes';
      if (signUpCompleted) {
        signUpCompleted({
          utagAccountSource,
          newsLetter: newsLetterVal,
        });
      }
      if (signUpMarketing) {
        signUpMarketing({
          source: entryOverlay,
          newsLetter: newsLetterVal,
        });
      }
      if (signupSuccess) signupSuccess();
      if (!getUrlParameter('code'))
        handleAnalytics({
          event: Events.PROFILE_SIGNUP_COMPLETED,
          payload: {
            account: newAccount,
            page_profile_type: this.fbAuthStarted
              ? SegmentPageProfileTypes.FACEBOOK
              : SegmentPageProfileTypes.GENERAL,
            page_start_source: utagAccountSource,
          },
          tealiumPayload: {
            ...getCommonTealiumProps(),
          },
        });
      if (this.handleActivateCode({ ...newAccount, isNew: true })) return;
      this.toggleSignup();
      this.toggleSignupSuccess();
    };
    this.setSignupErrors(hasErrors => {
      if (hasErrors || !register) {
        this.setState({ signUpEventFired: false });
        return;
      }
      const code = getActivateCodeFromURL() || getActivateCode();
      const { deviceId } = anonymousAccount || {};
      const toMigrate = deviceId ? { deviceId, migrateData: true } : {};
      const secondScreen = code ? { regcode: code } : {};
      const body = {
        password: signupPassword,
        email: signupEmail,
        firstName: signupFirstName,
        lastName: signupLastName,
        newsLetter: true,
        ...toMigrate,
        ...secondScreen,
      };

      register(body, result => {
        this.setState({ signUpEventFired: false });
        const { error, account: newAccount } = result;
        const errorCode = _.get(error, 'errorCode');
        const errorCodeString = _.toString(errorCode);
        if (error) {
          handleAnalytics({
            event: Events.PROFILE_SIGN_UP_ERROR,
            payload: {
              page_error_description: error.message || 'no error description',
              page_error_type: 'server-side',
              page_start_source: utagAccountSource,
            },
          });
        }
        switch (errorCodeString) {
          case EMAIL_INVALID:
            return this.setState({ signupGenericError: invalidEmailError });
          case EMAIL_IS_REGISTERED:
            return this.setState({ signupEmailExistsError: true });
          case EMAIL_IS_REGISTERED_TO_SOCIAL:
            return this.setState({ signupSocialEmailExistsError: true });
          default:
            break;
        }
        if (error) return this.setState({ signupGenericError: true });
        return handleSuccess(newAccount);
      });
    });
  }

  onForgotPasswordClick() {
    const { openForgotPassword } = this.props;
    if (openForgotPassword) {
      return openForgotPassword();
    }
    return this.toggleForgotPassword();
  }

  onForgotPassword() {
    const { reset, selectedNetwork } = this.props;
    const { signinEmail, showForgotPasswordLanding } = this.state;

    const handleSuccess = () => {
      this.toggleForgotPassword();
      this.toggleForgetPasswordSuccess();
      if (showForgotPasswordLanding) this.setState({ onCloseResetToBasePath: true });
    };

    this.setForgotPasswordErrors(hasErrors => {
      if (!hasErrors && reset) {
        const body = {
          email: signinEmail,
        };
        if (selectedNetwork) body.network = selectedNetwork;
        reset(body, result => {
          const { error } = result;
          if (error) return this.setState({ genericForgotPasswordError: error.message });
          return handleSuccess();
        });
      }
    });
  }

  onResetPassword() {
    const { accountEmail, resetConfirm, resetCode } = this.props;
    const { resetPasswordPassword, resetPasswordConfirmPassword } = this.state;

    const handleSuccess = () => {
      this.toggleResetPassword();
      this.toggleResetPasswordSuccess();
    };
    this.setResetPasswordErrors(hasErrors => {
      if (!hasErrors && resetConfirm && resetCode) {
        const body = {
          email: accountEmail,
          password: resetPasswordPassword,
          confirmPassword: resetPasswordConfirmPassword,
          code: resetCode,
        };
        resetConfirm(body, result => {
          const { error } = result;
          const message = _.get(error, 'message');
          if (error) return this.setState({ resetPasswordError: message });
          return handleSuccess();
        });
      }
    });
  }

  onVerificationCCPA = ({ shouldVerify = false, headerAuthorization }) => {
    const { verifyAccount, noVerifyAccount } = this.props;
    const { ccpaRequestId } = this.state;
    const handleVerification = shouldVerify ? verifyAccount : noVerifyAccount;
    if (ccpaRequestId) {
      handleVerification({ requestId: ccpaRequestId, headerAuthorization }, response => {
        const errorMessage = _.get(response, 'error.message');
        if (errorMessage) {
          this.setState({ signinGenericError: errorMessage });
        } else if (response && response.success) {
          this.setState({ onCloseResetToBasePath: true, showVerifyConfirmation: true });
        }
      });
    } else {
      this.setState({ signinGenericError: true });
    }
  };

  onSignin({ isCCPAVerify = false }) {
    const { login, signInCompleted, utagAccountSource, signInSuccess } = this.props;
    const { signinEmail, signinPassword } = this.state;

    if (isCCPAVerify) this.setState({ signinGenericError: null });

    const handleSuccess = account => {
      if (signInCompleted) signInCompleted({ utagAccountSource });
      if (!getUrlParameter('code'))
        handleAnalytics({
          event: Events.PROFILE_SIGNIN_COMPLETED,
          payload: {
            account,
            page_profile_type: this.fbAuthStarted
              ? SegmentPageProfileTypes.FACEBOOK
              : SegmentPageProfileTypes.GENERAL,
            page_start_source: utagAccountSource,
          },
          tealiumPayload: {
            ...getCommonTealiumProps(),
          },
        });
      if (this.handleActivateCode({ ...account, isNew: false })) return;
      if (signInSuccess) {
        if (isCCPAVerify) {
          this.onVerificationCCPA({
            shouldVerify: true,
            headerAuthorization: `Bearer ${account.accessToken}`,
          });
        }
        signInSuccess();
      }
      if (!isCCPAVerify) this.onClose();
    };

    this.setSigninErrors(hasErrors => {
      if (!hasErrors && login) {
        const body = {
          password: signinPassword,
          email: signinEmail,
        };
        login(body, result => {
          const { error, account } = result;
          if (error) {
            handleAnalytics({
              event: Events.PROFILE_SIGN_IN_ERROR,
              payload: {
                page_error_description: error.message || 'no error description',
                page_error_type: 'server-side',
                page_start_source: utagAccountSource,
              },
            });
            return this.setState({ signinGenericError: error.message });
          }
          return handleSuccess(account);
        });
      }
    });
  }

  onLinkFacebook() {
    const { showVerifyAccount } = this.state;
    const { login } = this.props;
    const { facebookLinkingEmail, facebookLinkingPassword, facebookLinkingToken } = this.state;
    const handleSuccess = () => {
      handleAnalytics({ event: Events.FB_LINKING_COMPLETED });
      if (showVerifyAccount) this.onVerificationCCPA({ shouldVerify: true });
      if (!showVerifyAccount) this.onClose();
    };
    this.setFacebookLinkingErrors(hasErrors => {
      if (!hasErrors && login) {
        const body = {
          password: facebookLinkingPassword,
          email: facebookLinkingEmail,
          facebookToken: facebookLinkingToken,
        };

        login(body, result => {
          const { error } = result;
          if (error) return this.setState({ facebookLinkingGenericError: error.message }); // eslint-disable-line
          return handleSuccess();
        });
      }
    });
  }

  onAppleSignIn = _.debounce(async appleToken => {
    const { login, register, checkStatus, utagAccountSource } = this.props;
    const { isEmailShared, userEmail } = this.state;
    const status = (await checkStatus({ userEmail })) || {};
    const body = { appleToken };
    if (status?.found) {
      register({ ...body, email: userEmail, link: true }, result => {
        const { error } = result;
        if (error) {
          login(body, result => this.onClose());
        } else {
          return this.onClose();
        }
        return null;
      });
      handleAnalytics({
        event: Events.PROFILE_SIGNIN_COMPLETED,
        payload: {
          page_profile_type: SegmentPageProfileTypes.APPLE,
          page_start_source: utagAccountSource,
        },
        tealiumPayload: {
          profile_type: SegmentPageProfileTypes.APPLE,
          page_start_source: utagAccountSource,
          ...getCommonTealiumProps(),
        },
      });
    } else {
      login(body, result => {
        const { error } = result;
        if (error) {
          const { is404Error } = error;
          if (is404Error && isEmailShared && !status?.found) {
            this.setState({ showCreatePassword: true, appleToken });
          } else if (is404Error && !isEmailShared) {
            this.setState({ showLinkAccount: true, appleToken });
          }
        } else {
          handleAnalytics({
            event: Events.PROFILE_SIGNIN_COMPLETED,
            payload: {
              page_profile_type: SegmentPageProfileTypes.APPLE,
              page_start_source: utagAccountSource,
            },
            tealiumPayload: {
              profile_type: SegmentPageProfileTypes.APPLE,
              page_start_source: utagAccountSource,
              ...getCommonTealiumProps(),
            },
          });
          return this.onClose();
        }
        return null;
      });
    }
  }, 500);

  onAppleLinking = _.debounce(link => {
    const { appleLinkingEmail, userEmail, createPasswordNewPassword, appleToken } = this.state;
    const { register, utagAccountSource } = this.props;
    const body = {
      additionalHeaders: v1Override,
      appleToken,
      email: appleLinkingEmail || userEmail,
      link,
      password: createPasswordNewPassword,
    };
    register(body, result => {
      const { error } = result;
      if (error) {
        const { message } = error;
        const { profile_genericInvalidError } = window.Language;
        // eslint-disable-next-line react/no-unused-state
        this.setState({ appleLinkingError: profile_genericInvalidError || message });
      } else {
        handleAnalytics(
          {
            event: link
              ? Events.PROFILE_APPLE_ID_LINK_COMPLETED
              : Events.PROFILE_APPLE_ID_SIGNUP_COMPLETED,
            payload: {
              page_profile_type: SegmentPageProfileTypes.APPLE,
              page_start_source: utagAccountSource,
            },
            tealiumPayload: {
              profile_type: SegmentPageProfileTypes.APPLE,
              page_start_source: utagAccountSource,
              ...getCommonTealiumProps(),
            },
          },
          {
            event: link ? Events.PROFILE_SIGNIN_COMPLETED : Events.PROFILE_SIGNUP_COMPLETED,
            payload: {
              page_profile_type: SegmentPageProfileTypes.APPLE,
              page_start_source: utagAccountSource,
            },
            tealiumPayload: {
              profile_type: SegmentPageProfileTypes.APPLE,
              page_start_source: utagAccountSource,
              ...getCommonTealiumProps(),
            },
          },
        );

        return this.onClose();
      }
      return null;
    });
  }, 500);

  onRegisterWithGoogle = _.debounce(() => {
    const { createPasswordNewPassword } = this.state;

    const {
      register,
      utagAccountSource,
      googleSignIn: { isGoogleSignIn },
      googleOneTap: { isGoogleOneTap, googleOneTapEmail, googleToken: oneTapToken },
      googleNewUser: { googleEmail, googleToken: signUpToken },
    } = this.props;

    const googleToken = isGoogleSignIn ? signUpToken : oneTapToken;

    const body = {
      googleToken,
      email: isGoogleSignIn ? googleEmail : googleOneTapEmail,
      isGoogleSignIn,
      isGoogleOneTap,
      password: createPasswordNewPassword,
    };

    register(body, result => {
      const { error } = result;
      if (error) {
        const { message } = error;
        // eslint-disable-next-line react/no-unused-state
        this.setState({ googleLinkingError: message });
      } else {
        handleAnalytics(
          {
            event: Events.PROFILE_GOOGLE_LINK_COMPLETED,
            payload: {
              page_profile_type: SegmentPageProfileTypes.GOOGLE,
              page_start_source: utagAccountSource,
            },
            tealiumPayload: {
              profile_type: SegmentPageProfileTypes.GOOGLE,
              page_start_source: utagAccountSource,
              ...getCommonTealiumProps(),
            },
          },
          {
            event: Events.PROFILE_SIGNUP_COMPLETED,
            payload: {
              page_profile_type: SegmentPageProfileTypes.GOOGLE,
              page_start_source: utagAccountSource,
            },
            tealiumPayload: {
              profile_type: SegmentPageProfileTypes.GOOGLE,
              page_start_source: utagAccountSource,
              ...getCommonTealiumProps(),
            },
          },
        );
        return this.onClose();
      }
      return null;
    });
  }, 500);

  onFieldChange(event) {
    const { target } = event;
    const isCheckbox = target.type === 'checkbox';
    const value = isCheckbox ? target.checked : target.value;
    const { name } = target;
    this.setState({ [name]: value });
  }

  onDeleteAccount() {
    const { deleteAccount, account } = this.props;
    const profileId = account.profileId || null;
    const handleSuccess = () => {
      this.onLogout();
    };
    if (deleteAccount && profileId) {
      const body = { profileId };
      deleteAccount(body, result => {
        const { error } = result;
        if (error) return this.setState({ deleteAccountGenericError: error.message }); // eslint-disable-line
        return handleSuccess();
      });
    }
  }

  setSigninErrors(cb, name) {
    const { signinPassword, signinEmail } = this.state;
    const { utagAccountSource } = this.props;
    const signinPasswordError = passwordErrors(signinPassword);
    const signinEmailError = !isValidEmail(signinEmail);
    const setStateCb = () => {
      const { signinPasswordError, signinEmailError } = this.state;
      const hasErrors = signinPasswordError || signinEmailError || false;
      if (hasErrors) {
        let errorDescription;
        if (signinPasswordError) {
          errorDescription = 'Password Error';
        } else if (signinEmailError) {
          errorDescription = 'Email Error';
        } else {
          errorDescription = 'no error description';
        }

        handleAnalytics({
          event: Events.PROFILE_SIGN_IN_ERROR,
          payload: {
            page_error_description: errorDescription,
            page_error_type: 'client-side',
            page_start_source: utagAccountSource,
          },
        });
      }
      if (cb) return cb(hasErrors);
      return hasErrors;
    };

    if (name) {
      switch (name) {
        case 'signinPassword':
          this.setState({ signinPasswordError }, setStateCb);
          break;
        case 'signinEmail':
          this.setState({ signinEmailError }, setStateCb);
          break;
        default:
          break;
      }
    } else {
      return this.setState(
        {
          signinPasswordError,
          signinEmailError,
        },
        setStateCb,
      );
    }
    return null;
  }

  setForgotPasswordErrors(cb, name) {
    const { signinEmail } = this.state;
    const forgotPasswordError = !isValidEmail(signinEmail);
    const setStateCb = () => {
      const { forgotPasswordError } = this.state;
      const hasErrors = forgotPasswordError || false;
      if (cb) return cb(hasErrors);
      return hasErrors;
    };
    let genericForgotPasswordError;

    if (name) {
      switch (name) {
        case 'forgotPasswordError':
          this.setState({ forgotPasswordError }, setStateCb);
          break;
        default:
          break;
      }
    } else {
      return this.setState(
        {
          forgotPasswordError,
          genericForgotPasswordError,
        },
        setStateCb,
      );
    }
    return null;
  }

  setResetPasswordErrors(cb, name) {
    const { resetPasswordPassword, resetPasswordConfirmPassword } = this.state;
    const resetPasswordPasswordError = !isValidPassword(resetPasswordPassword);
    const resetPasswordConfirmPasswordError =
      !isValidPassword(resetPasswordConfirmPassword) &&
      resetPasswordPassword !== resetPasswordConfirmPassword;

    const setStateCb = () => {
      const { resetPasswordPasswordError, resetPasswordConfirmPasswordError } = this.state;
      const hasErrors = resetPasswordPasswordError || resetPasswordConfirmPasswordError || false;
      if (cb) return cb(hasErrors);
      return hasErrors;
    };

    if (name) {
      switch (name) {
        case 'resetPasswordPasswordError':
          this.setState({ resetPasswordPasswordError }, setStateCb);
          break;
        case 'resetPasswordConfirmPasswordError':
          this.setState({ resetPasswordConfirmPasswordError }, setStateCb);
          break;
        default:
          break;
      }
    } else {
      return this.setState(
        {
          resetPasswordPasswordError,
          resetPasswordConfirmPasswordError,
        },
        setStateCb,
      );
    }
    return null;
  }

  setSignupErrors(cb) {
    const { signupFirstName, signupLastName, signupPassword, signupEmail } = this.state;
    const { utagAccountSource } = this.props;
    const signupPasswordError = !isValidPassword(signupPassword);
    const signupEmailError = !isValidEmail(signupEmail);
    const signupFirstNameError = _.isEmpty(signupFirstName.trim());
    const signupLastNameError = _.isEmpty(signupLastName.trim());
    const signupFullNameError = signupFirstNameError && signupLastNameError;
    const missingFieldDataError =
      signupFirstNameError || signupLastNameError || signupPasswordError || signupEmailError;

    return this.setState(
      {
        signupFirstNameError,
        signupLastNameError,
        signupFullNameError,
        signupPasswordError,
        signupEmailError,
      },
      () => {
        const hasErrors = missingFieldDataError || false;
        if (hasErrors) {
          const errorDescription = 'Missing Field Data';
          handleAnalytics({
            event: Events.PROFILE_SIGN_UP_ERROR,
            payload: {
              page_error_description: errorDescription,
              page_error_type: 'client-side',
              page_start_source: utagAccountSource,
            },
          });
        }
        if (cb) return cb(hasErrors);
        return hasErrors;
      },
    );
  }

  setIsAppleEmailShared = isEmailShared => {
    this.setState({ isEmailShared });
  };

  setAppleToken = appleToken => {
    this.setState({ appleToken });
  };

  setAppleEmail = email => {
    this.setState({ userEmail: email });
  };

  setAppleLinkingEmail = email => {
    this.setState({ appleLinkingEmail: email });
  };

  setLinkingEmail(email) {
    this.setState({ facebookLinkingEmail: email });
  }

  setLinkingToken(token) {
    this.setState({ facebookLinkingToken: token });
  }

  toggleAccountMain(showAccount) {
    const { showAccountMain, showPerks } = this.state;
    this.setState({ showAccountMain: showAccount ? true : !showAccountMain });
    if (!showAccountMain && !showPerks) {
      handleAnalytics({
        event: Events.SETTINGS_SECTION_LANDING,
        payload: {
          pageName: `settings:landing`,
          pageType: 'settings:section-front',
        },
      });
    }
  }

  resetSignupState(cb) {
    this.setState({ ...DEFAULT_SIGNUP_STATE }, cb);
  }

  resetSigninState(cb) {
    this.setState({ ...DEFAULT_SIGNIN_STATE }, cb);
  }

  resetAll(cb) {
    this.setState({ ...DEFAULT_STATE }, cb);
  }

  setLinkingEmailAndToken = (email, token) => {
    this.setLinkingEmail(email);
    this.setLinkingToken(token);
  };

  async onSocialFacebookLogin() {
    const {
      language: { profile_emailRequirmentNoticeSignIn },
      socialFacebookLogin,
    } = this.props;
    const handle202 = () => {
      this.toggleFacebookLinking();
    };
    const handle404001 = ({ facebookEmail, facebookToken }) => {
      this.setState(
        {
          facebookToken,
          facebookEmail,
        },
        () => {
          this.toggleFacebookLoginPasswordLink();
        },
      );
    };

    const setFacebookGenericError = message => {
      this.setState({ facebookGenericError: message });
    };
    handleAnalytics({ event: Events.FB_SIGN_IN_STARTED });
    this.fbAuthStarted = true;

    if (socialFacebookLogin) {
      try {
        const response = await facebookLogin({});
        const facebookToken = _.get(response, 'authResponse.accessToken');
        FB.api('/me', { fields: 'email' }, response => {
          const facebookEmail = _.get(response, 'email');
          this.setLinkingEmailAndToken(facebookEmail, facebookToken);

          socialFacebookLogin({ facebookToken }, result => {
            const { showVerifyAccount } = this.state;
            const { error } = result;
            if (error) {
              const errorCode = _.get(error, 'errorCode');
              const needsAccountLinking = errorCode === 202;
              const needsSignup = errorCode === 4040001;
              const needsEmail = errorCode === 412;
              if (needsEmail || !facebookEmail) {
                return setFacebookGenericError(profile_emailRequirmentNoticeSignIn);
              }
              if (needsAccountLinking) return handle202();
              if (needsSignup) return handle404001({ facebookEmail, facebookToken });
            }
            if (showVerifyAccount) this.onVerificationCCPA({ shouldVerify: true });
            handleAnalytics({ event: Events.FB_SIGN_IN_COMPLETED });
            if (!showVerifyAccount) this.onClose();
            return null;
          });
        });
      } catch (error) {
        console.error(error);
      }
    }
  }

  async onSocialFacebookRegister() {
    const { socialFacebookRegister } = this.props;

    const handleSuccess = () => {
      handleAnalytics({ event: Events.FB_SIGN_UP_COMPLETED });
      this.setState({
        showFacebookPasswordLink: false,
        showFacebookLoginPasswordLink: false,
        showSignupSuccess: true,
      });
    };
    const setGenericError = message => {
      this.setState({ facebookPasswordLinkGenericError: message });
    };
    const { facebookPasswordLinkPassword: password } = this.state;
    const facebookPasswordLinkPasswordError = !isValidPassword(password);
    this.setState({ facebookPasswordLinkPasswordError });
    const { facebookToken, facebookEmail: email } = this.state;
    socialFacebookRegister(
      {
        additionalHeaders: v1Override,
        email,
        facebookToken,
        newsLetter: true,
        password,
      },
      result => {
        const { error, success, account } = result;
        if (error) return setGenericError(_.get(error, 'message'));
        if (this.handleActivateCode(account)) return null;
        if (success) handleSuccess();
        return null;
      },
    );
  }

  onShowFacebookPasswordLink() {
    this.toggleSignup();
    this.toggleFacebookPasswordLink();
  }

  renderAccountMain() {
    const { language, backgroundClass, favoritesManager } = this.props;
    const { profile_personalizedHeader: personalizedHeader, profile_header: header } =
      language || {};
    const firstName = _.get(this.props, 'account.firstName', null);
    const headerText = firstName ? `${firstName}${personalizedHeader}` : header;
    const onClose = opts => this.onClose(opts);
    const headerProps = {
      headerText,
      noIcon: false,
      onClose,
      renderLogo: this.renderLogo,
      showHeaderDesktop: true,
    };
    const accountMainProps = {
      backgroundClass,
      favoritesManager,
      firstName,
      headerProps,
      toggleAccountMain: this.toggleAccountMain,
      toggleChangePassword: this.toggleChangePassword,
      toggleEditAccount: this.toggleEditAccount,
      toggleManageFavorites: this.toggleManageFavorites,
      toggleParentalControls: this.toggleParentalControls,
      toggleSignOut: this.toggleSignOut,
    };
    const { showAccountMain } = this.state;
    if (!showAccountMain) return null;
    return <AccountMain {...accountMainProps} />;
  }

  renderVerifyAccount() {
    const {
      backgroundClass,
      facebookLoginOptionalText,
      facebookLoginText,
      facebookSignupOptionalText,
      facebookSignupText,
      language,
      privacyPolicyClickEvent,
      showFacebookLogin,
      showFacebookSignup,
      signInForgotPasswordStarted,
      signInIncomplete,
      signinDropdownIconTheme,
      utagAccountSource,
    } = this.props;
    const { profileSignin_header: headerText, profileSignin_forgotPasswordText: linkText } =
      language || {};
    const {
      facebookGenericError,
      showVerifyAccount,
      showVerifyConfirmation,
      signinEmail,
      signinEmailError,
      signinGenericError,
      signinMigrationDropdownOpen,
      signinPassword,
      signinPasswordError,
      signinShowPassword,
    } = this.state;
    const onClose = opts => {
      this.onClose(opts);
      handleAnalytics({
        event: Events.PROFILE_SIGN_IN_STARTED,
        payload: { utagAccountSource, onboardingAction: SKIP },
      });
      signInIncomplete({ utagAccountSource });
      // This needs to be external to load back in trackers if they do not verify
      redirectToExternalUrl(helperGetHomeUrl());
    };
    const headerProps = {
      onClose,
      renderLogo: this.renderLogo,
      headerText,
    };
    const ctaProps = {
      text: `Don't Have an Account?`,
      onClick: () => {
        this.onVerificationCCPA({ shouldVerify: false });
      },
    };
    const forgotPasswordCTAProps = {
      linkText,
      onClick: () => {
        this.setState({ showVerifyAccount: false, fromVerifyAccount: true });
        if (this.onForgotPasswordClick) this.onForgotPasswordClick();
        if (signInForgotPasswordStarted) signInForgotPasswordStarted({ utagAccountSource });
      },
    };
    const verifyAccountProps = {
      backgroundClass,
      ctaProps,
      facebookGenericError,
      facebookLoginOptionalText,
      facebookLoginText,
      facebookSignupOptionalText,
      facebookSignupText,
      forgotPasswordCTAProps,
      headerProps,
      language,
      onFieldChange: this.onFieldChange,
      onSignin: this.onSignin,
      onSocialFacebookLogin: this.onSocialFacebookLogin,
      privacyPolicyClickEvent,
      showFacebookLogin,
      showFacebookSignup,
      showVerifyConfirmation,
      signinDropdownIconTheme,
      signinEmail,
      signinEmailError,
      signinGenericError,
      signinMigrationDropdownOpen,
      signinPassword,
      signinPasswordError,
      signinShowPassword,
    };
    if (!showVerifyAccount) return null;
    return <VerifyAccount {...verifyAccountProps} />;
  }

  renderShareMyEmailUpsell = () => {
    const { showShareMyEmailUpsell } = this.state;
    if (!showShareMyEmailUpsell) return null;

    return (
      <ShareMyEmailUpsell
        onAppleSignIn={this.onAppleSignIn}
        onAppleLinking={(email, password, token) => this.onAppleLinking(email, password, token)}
        setAppleEmail={email => this.setAppleEmail(email)}
        setAppleToken={token => this.setAppleToken(token)}
        setIsAppleEmailShared={isEmailShared => this.setIsAppleEmailShared(isEmailShared)}
      />
    );
  };

  renderDeleteAccount() {
    const { language } = this.props;
    const headerProps = {
      renderLogo: this.renderLogo,
      noIcon: false,
      onClose: opts => this.onClose(opts),
      showHeaderDesktop: true,
    };
    const deleteAccountProps = {
      headerProps,
      language,
      onClose: opts => this.onClose({ ...opts, ignoreSavedPath: true }),
      onDeleteAccount: this.onDeleteAccount,
      renderLogo: this.renderLogo,
    };

    const { showDeleteAccount } = this.state;
    if (!showDeleteAccount) return null;
    return <DeleteAccount {...deleteAccountProps} />;
  }

  renderForgotPassword() {
    const { autoFocus, backgroundClass, language, network } = this.props;
    const { profileResetPw_header: headerText } = language || {};
    const {
      forgotPasswordError,
      fromVerifyAccount,
      genericForgotPasswordError,
      showForgotPassword,
      showForgotPasswordLanding,
      signinEmail,
    } = this.state;
    const onClose = opts => {
      if (fromVerifyAccount) {
        this.setState({
          showVerifyAccount: true,
          showForgotPassword: false,
          showForgotPasswordLanding: false,
        });
      } else {
        this.onClose(opts);
      }
    };
    const back = (
      <button
        onClick={opts => onClose({ ...opts, ignoreSavedPath: true })}
        className={Style.back}
      />
    );

    const headerProps = {
      headerText,
      iconMobile: back,
      isDesktop: false,
      onClose,
      renderLogo: this.renderLogo,
    };
    const forgotPasswordProps = {
      autoFocus,
      backgroundClass,
      forgotPasswordError,
      genericForgotPasswordError,
      headerProps,
      language,
      network,
      onFieldChange: this.onFieldChange,
      onForgotPassword: this.onForgotPassword,
      signinEmail,
    };
    if (!showForgotPassword && !showForgotPasswordLanding) return null;
    return <ForgotPassword {...forgotPasswordProps} />;
  }

  renderForgotPasswordSuccess() {
    const { backgroundClass, language, network } = this.props;
    const forgotPasswordSuccessProps = {
      backgroundClass,
      language,
      network,
      onClose: this.onClose,
    };
    const { showForgotPasswordSuccess } = this.state;
    if (!showForgotPasswordSuccess) return null;
    return <ForgotPasswordSuccess {...forgotPasswordSuccessProps} />;
  }

  renderLogout() {
    const { backgroundClass, language, loader } = this.props;
    const { logoutInProcess, showSignOut, logoutTimedOut } = this.state;

    const logoutProps = {
      backgroundClass,
      language,
      loader,
      logoutInProcess,
      onClose: opts => this.onClose(opts),
      onLogout: this.onLogout,
      renderLoader: logoutTimedOut,
      renderLogo: this.renderLogo,
    };
    if (!showSignOut) return null;
    return <Logout {...logoutProps} />;
  }

  renderManageFavorites() {
    const {
      backgroundClass,
      breakpoint,
      favoritesManager,
      isMobile,
      language,
      renderFavorites,
    } = this.props;
    const { profileManageFavorites_header: headerText } = language || {};
    const onTitleClick = () => {
      this.onClose({ ignoreRedirect: true });
    };
    let favorites;
    if (renderFavorites)
      favorites = renderFavorites({
        breakpoint,
        favoritesManager,
        onTitleClick,
        onTileClick: this.onClose,
      });
    const back = <button onClick={this.onFavoritesBackClick} className={Style.back} />;
    const headerProps = {
      headerText,
      iconMobile: back,
      isDesktop: !isMobile,
      onClose: opts => this.onClose(opts),
      showHeaderDesktop: true,
      renderLogo: this.renderLogo,
    };
    const { showManageFavorites } = this.state;
    const manageFavoritesProps = {
      headerProps,
      backgroundClass,
      favorites,
    };
    if (!showManageFavorites) return null;
    return <ManageFavorites {...manageFavoritesProps} />;
  }

  renderConsolidatedSignInFlow() {
    mountSessionCam();
    const {
      checkStatus,
      facebookLoginOptionalText,
      facebookLoginText,
      facebookSignupOptionalText,
      facebookSignupText,
      language,
      login,
      perks,
      register,
      renderPerkImage,
      reset,
      showFacebookSignup,
      showForgotPasswordLanding,
      signInCompleted,
      signInSuccess,
      utagAccountSource,
    } = this.props;
    const { facebookGenericError, signinPassword, signinShowPassword } = this.state;

    const { profile_facebookLoginTitle: headerText } = language || {};
    const onClose = opts => {
      this.onClose(opts);
      handleAnalytics({ event: Events.PROFILE_SCREEN_BACKED_OUT });
    };

    const headerProps = {
      headerText,
      onClose,
      renderLogo: this.renderLogo,
      showBorder: true,
    };
    const consolidatedSignInFlowProps = {
      checkStatus,
      facebookGenericError,
      facebookLoginOptionalText,
      facebookLoginText,
      facebookSignupOptionalText,
      facebookSignupText,
      handleActivateCode: this.handleActivateCode,
      handleShowShareMyEmailUpsell: this.handleShowShareMyEmailUpsell,
      headerProps,
      language,
      onAppleSignIn: this.onAppleSignIn,
      register,
      toggleSignupSuccess: this.toggleSignupSuccess,
      onAppleLinking: this.onAppleLinking,
      onClose: this.onClose,
      onFieldChange: this.onFieldChange,
      onRegisterWithGoogle: this.onRegisterWithGoogle,
      onSocialFacebookLogin: this.onSocialFacebookLogin,
      login,
      perks,
      renderPerkImage,
      reset,
      setAppleEmail: this.setAppleEmail,
      setAppleToken: this.setAppleToken,
      setIsAppleEmailShared: this.setIsAppleEmailShared,
      showFacebookSignup,
      signInCompleted,
      signinPassword,
      signinShowPassword,
      signInSuccess,
      toggleCreatePassword: this.toggleCreatePassword,
      utagAccountSource,
    };
    const { showPerks } = this.state;
    if (!showPerks || !perks || showForgotPasswordLanding) return null;
    return <ConsolidatedSignInFlow {...consolidatedSignInFlowProps} />;
  }

  renderResetPassword() {
    const {
      accountEmail,
      autoFocus,
      backgroundClass,
      closeAccount,
      language,
      network,
    } = this.props;
    const { profileResetPw_header: headerText } = language || {};
    const {
      showResetPassword,
      resetPasswordShowPassword,
      resetPasswordShowConfirmPassword,
      resetPasswordError,
      resetPasswordPasswordError,
      resetPasswordPassword,
      resetPasswordConfirmPasswordError,
      resetPasswordConfirmPassword,
    } = this.state;

    const back = <button onClick={closeAccount} className={Style.back} />;
    const onClose = () => {
      this.onClose({ ignoreRedirect: true });
    };
    const headerProps = {
      headerText,
      showHeaderDesktop: true,
      hideCloseButton: true,
      iconMobile: back,
      isDesktop: false,
      noIcon: true,
      renderLogo: this.renderLogo,
      onClose,
    };
    const resetPasswordProps = {
      autoFocus,
      accountEmail,
      backgroundClass,
      closeAccount,
      headerProps,
      language,
      network,
      onFieldChange: this.onFieldChange,
      onResetPassword: this.onResetPassword,
      resetPasswordError,
      resetPasswordPassword,
      resetPasswordPasswordError,
      resetPasswordShowPassword,
      resetPasswordConfirmPassword,
      resetPasswordConfirmPasswordError,
      resetPasswordShowConfirmPassword,
    };
    if (!showResetPassword) return null;
    return <ResetPassword {...resetPasswordProps} />;
  }

  renderResetPasswordSuccess() {
    const { backgroundClass, language, network } = this.props;

    const resetPasswordSuccessProps = {
      backgroundClass,
      language,
      network,
      onClose: this.onClose,
    };

    const { showResetPasswordSuccess } = this.state;
    if (!showResetPasswordSuccess) return null;
    return <ResetPasswordSuccess {...resetPasswordSuccessProps} />;
  }

  renderActivationSuccess = () => {
    const { showActivationSuccess } = this.state;
    if (!showActivationSuccess) return null;
    return <ActivationSuccess />;
  };

  renderSignupSuccess() {
    const {
      backgroundClass,
      language,
      optimizelyLanguageStrings,
      toggleMvpdAuthOverlay,
      useMVPDLoginProfile,
      account,
    } = this.props;
    const onClose = () => this.onClose({ dontFetchFavorites: true });
    const onMvpdStart = () => (useMVPDLoginProfile ? toggleMvpdAuthOverlay() : null);
    const signupSuccessProps = {
      backgroundClass,
      language,
      onClose,
      onMvpdStart,
      optimizelyLanguageStrings,
      renderLogo: this.renderLogo,
      useMVPDLoginProfile,
    };

    const { showSignupSuccess } = this.state;
    if (!showSignupSuccess) return null;

    if (this.handleActivateCode({ ...account, isNew: true })) return null;

    return <SignupSuccess {...signupSuccessProps} />;
  }

  renderSignin() {
    const {
      backgroundClass,
      facebookLoginOptionalText,
      facebookLoginText,
      facebookSignupOptionalText,
      facebookSignupText,
      getUseUtagGlobal,
      language,
      mvpdData,
      pageViewEvent,
      privacyPolicyClickEvent,
      showFacebookLogin,
      showFacebookSignup,
      signInForgotPasswordStarted,
      signInIncomplete,
      signinDropdownIconTheme,
      utagAccountSource,
    } = this.props;
    const {
      profileSignin_header: headerText,
      profileSignin_forgotPasswordText: linkText,
      profileSignin_createProfilePrompt: text,
      profileSignin_createProfileCta: ctaLinkText,
    } = language || {};
    const {
      facebookGenericError,
      showSignin,
      signinEmail,
      signinEmailError,
      signinGenericError,
      signinMigrationDropdownOpen,
      signinPassword,
      signinPasswordError,
      signinShowPassword,
    } = this.state;
    const onClose = opts => {
      this.onClose(opts);
      signInIncomplete({ utagAccountSource });
    };
    const headerProps = {
      onClose,
      renderLogo: this.renderLogo,
      headerText,
    };
    const ctaProps = {
      text,
      linkText: ctaLinkText,
      onClick: () => {
        if (this.onSigninRegisterClick) this.onSigninRegisterClick();
        handleAnalytics({
          event: Events.PROFILE_SIGNUP_STATRED,
        });
        logEvent('onProfileSignUp');
      },
    };
    const forgotPasswordCTAProps = {
      linkText,
      onClick: () => {
        if (this.onForgotPasswordClick) this.onForgotPasswordClick();
        if (signInForgotPasswordStarted) signInForgotPasswordStarted({ utagAccountSource });
      },
    };

    const signinProps = {
      backgroundClass,
      ctaProps,
      facebookGenericError,
      facebookLoginOptionalText,
      facebookLoginText,
      facebookSignupOptionalText,
      facebookSignupText,
      forgotPasswordCTAProps,
      getUseUtagGlobal,
      headerProps,
      language,
      mvpdData,
      onAppleLinking: (email, password, token) => this.onAppleLinking(email, password, token),
      onAppleSignIn: token => this.onAppleSignIn(token),
      onClose,
      onFieldChange: this.onFieldChange,
      onRegisterWithGoogle: (email, password, token) =>
        this.onRegisterWithGoogle(email, password, token),
      onSignin: this.onSignin,
      onSocialFacebookLogin: this.onSocialFacebookLogin,
      pageViewEvent,
      privacyPolicyClickEvent,
      setAppleEmail: email => this.setAppleEmail(email),
      setAppleToken: appleToken => this.setAppleToken(appleToken),
      setIsAppleEmailShared: isEmailShared => this.setIsAppleEmailShared(isEmailShared),
      showFacebookLogin,
      showFacebookSignup,
      showSignin,
      signinDropdownIconTheme,
      signinEmail,
      signinEmailError,
      signinGenericError,
      signinMigrationDropdownOpen,
      signinPassword,
      signinPasswordError,
      signinShowPassword,
      toggleCreatePassword: this.toggleCreatePassword,
      utagAccountSource,
    };
    if (!showSignin) return null;
    return <Signin {...signinProps} />;
  }

  renderSignup() {
    const {
      backgroundClass,
      facebookLoginOptionalText,
      facebookLoginText,
      facebookSignupOptionalText,
      facebookSignupText,
      getUseUtagGlobal,
      language,
      mvpdData,
      pageViewEvent,
      privacyPolicyClickEvent,
      showFacebookSignup,
      signUpIncomplete,
      utagAccountSource,
    } = this.props;
    const {
      global_signInButton: linkText,
      profileCreate_header: headerText,
      profileCreate_signInText: text,
    } = language || {};
    const {
      entryOverlay,
      facebookGenericError,
      showSignup,
      signupEmail,
      signupEmailError,
      signupEmailExistsError,
      signupFirstName,
      signupFirstNameError,
      signupFullNameError,
      signupGenericError,
      signupLastName,
      signupLastNameError,
      signupPassword,
      signupPasswordError,
      signupShowPassword,
      signupSocialEmailExistsError,
      signUpEventFired,
    } = this.state;
    const onClose = opts => {
      this.onClose(opts);
      signUpIncomplete({ utagAccountSource });
    };
    const headerProps = {
      headerText,
      onClose,
      renderLogo: this.renderLogo,
    };
    const ctaProps = {
      text,
      linkText,
      onClick: () => {
        if (this.onSignupLoginClick) this.onSignupLoginClick();
        handleAnalytics({
          event: Events.PROFILE_SIGN_IN_STARTED,
        });
        logEvent('onProfileSignIn');
      },
    };

    const signupProps = {
      backgroundClass,
      ctaProps,
      entryOverlay,
      facebookGenericError,
      facebookLoginOptionalText,
      facebookLoginText,
      facebookSignupOptionalText,
      facebookSignupText,
      getUseUtagGlobal,
      handleShowShareMyEmailUpsell: this.handleShowShareMyEmailUpsell,
      headerProps,
      language,
      mvpdData,
      onAppleLinking: (email, password, token) => this.onAppleLinking(email, password, token),
      onRegisterWithGoogle: (email, password, token) =>
        this.onRegisterWithGoogle(email, password, token),
      onAppleSignIn: token => this.onAppleSignIn(token),
      onClose,
      onSocialFacebookLogin: this.onSocialFacebookLogin,
      onFieldChange: this.onFieldChange,
      onSignup: this.onSignup,
      pageViewEvent,
      privacyPolicyClickEvent,
      setAppleEmail: email => this.setAppleEmail(email),
      setAppleToken: appleToken => this.setAppleToken(appleToken),
      setIsAppleEmailShared: isEmailShared => this.setIsAppleEmailShared(isEmailShared),
      showFacebookSignup,
      signUpEventFired,
      signUpIncomplete,
      signupEmail,
      signupEmailError,
      signupEmailExistsError,
      signupFirstName,
      signupFirstNameError,
      signupFullNameError,
      signupGenericError,
      signupLastName,
      signupLastNameError,
      signupPassword,
      signupPasswordError,
      signupShowPassword,
      signupSocialEmailExistsError,
      toggleCreatePassword: this.toggleCreatePassword,
      utagAccountSource,
    };
    if (!showSignup) return null;
    return <Signup {...signupProps} />;
  }

  renderSocialFacebookPasswordLink() {
    const { language, backgroundClass } = this.props;
    const { profile_facebookLoginTitle: headerText } = language || {};
    const {
      facebookEmail,
      facebookPasswordLinkGenericError,
      facebookPasswordLinkPassword,
      facebookPasswordLinkPasswordError,
      facebookPasswordLinkShowPassword,
      showFacebookLoginPasswordLink,
      showFacebookPasswordLink,
    } = this.state;
    const handleClick = () => {
      if (showFacebookPasswordLink)
        handleAnalytics({ event: Events.FB_PASSWORD_CREATION_COMPLETED });
      this.onSocialFacebookRegister();
    };
    const onClose = () => {
      this.onClose();
      this.setState({ ...DEFAULT_FACEBOOK_PASSWORD_LINKING_STATE });
    };
    const headerProps = {
      headerText,
      onClose,
      renderLogo: this.renderLogo,
    };
    const socialFacebookPasswordLinkProps = {
      backgroundClass,
      facebookEmail,
      facebookPasswordLinkGenericError,
      facebookPasswordLinkPassword,
      facebookPasswordLinkPasswordError,
      facebookPasswordLinkShowPassword,
      handleClick,
      headerProps,
      language,
      onFieldChange: this.onFieldChange,
    };

    if (!showFacebookPasswordLink && !showFacebookLoginPasswordLink) return null;
    return <SocialFacebookPasswordLink {...socialFacebookPasswordLinkProps} />;
  }

  render() {
    const { showAccount, appView, isAppViewExcluded } = this.props;
    if (!showAccount || (appView && isAppViewExcluded(NAME))) return null;
    return (
      <>
        {this.renderAccountMain()}
        {this.renderDeleteAccount()}
        {this.renderForgotPassword()}
        {this.renderForgotPasswordSuccess()}
        {this.renderLogout()}
        {this.renderManageFavorites()}
        {this.renderConsolidatedSignInFlow()}
        {this.renderResetPassword()}
        {this.renderResetPasswordSuccess()}
        {this.renderSignin()}
        {this.renderSignup()}
        {this.renderActivationSuccess()}
        {this.renderShareMyEmailUpsell()}
        {this.renderSignupSuccess()}
        {this.renderSocialFacebookPasswordLink()}
        {this.renderAppleLinking()}
        {this.renderEditAccount()}
        {this.renderChangePassword()}
        {this.renderCreatePassword()}
        {this.renderFacebookLinking()}
        {this.renderParentalControls()}
        {this.renderVerifyAccount()}
      </>
    );
  }
}

Account.defaultProps = {
  account: null,
  accountEmail: '',
  accountExpired: null,
  anonymousAccount: null,
  appView: false,
  autoFocus: false,
  backgroundClass: null,
  breakpoint: null,
  checkStatus: noop,
  createPasswordPageEvent: noop,
  closeAccount: noop,
  deleteAccount: null,
  facebookLoginOptionalText: '',
  facebookLoginText: '',
  facebookSignupOptionalText: '',
  facebookSignupText: '',
  favoritesManager: {},
  getUseUtagGlobal: noop,
  googleSignIn: {},
  ignoreOnCloseRedirect: false,
  initialScreen: '',
  isAppViewExcluded: noop,
  isGoogleOneTapDisplayed: null,
  isMobile: false,
  isTablet: false,
  language: {},
  loader: null,
  login: null,
  logout: noop,
  mvpdData: null,
  network: [],
  noVerifyAccount: noop,
  onLoginClick: noop,
  onSignupClick: noop,
  onboardingStart: noop,
  openAccount: noop,
  openForgotPassword: null,
  optimizelyLanguageStrings: noop,
  pageViewEvent: noop,
  params: {},
  perks: [],
  perksPresented: noop,
  previousPath: null,
  privacyPolicyClickEvent: {},
  register: null,
  renderFavorites: null,
  renderNetworkLink: noop,
  renderPerkImage: noop,
  reset: null,
  resetAccountState: noop,
  resetCode: null,
  resetConfirm: null,
  selectedNetwork: null,
  setAccountCloseIgnoreRedirect: noop,
  showAccount: false,
  showFacebookLogin: false,
  showFacebookSignup: false,
  showForgotPasswordLanding: false,
  signInCompleted: null,
  signInError: noop,
  signInForgotPasswordStarted: null,
  signInIncomplete: noop,
  signInSuccess: null,
  signUpCompleted: null,
  signUpError: noop,
  signUpIncomplete: noop,
  signUpMarketing: null,
  signinDropdownIconTheme: {},
  signupSuccess: null,
  socialFacebookLogin: null,
  socialFacebookRegister: noop,
  toggleAccountExpired: noop,
  toggleMvpdAuthOverlay: noop,
  useMVPDLoginProfile: false,
  utagAccountSource: null,
  verifyAccount: noop,
};

Account.propTypes = {
  account: PropTypes.shape({}),
  accountEmail: PropTypes.string,
  accountExpired: PropTypes.bool,
  anonymousAccount: PropTypes.shape({}),
  appView: PropTypes.bool,
  autoFocus: PropTypes.bool,
  backgroundClass: PropTypes.string,
  breakpoint: PropTypes.string,
  checkStatus: PropTypes.func,
  createPasswordPageEvent: PropTypes.func,
  closeAccount: PropTypes.func,
  deleteAccount: PropTypes.func,
  facebookLoginOptionalText: PropTypes.string,
  facebookLoginText: PropTypes.string,
  facebookSignupOptionalText: PropTypes.string,
  facebookSignupText: PropTypes.string,
  favoritesManager: PropTypes.shape({}),
  getUseUtagGlobal: PropTypes.func,
  googleNewUser: PropTypes.shape({}),
  googleOneTap: PropTypes.shape({}),
  googleSignIn: PropTypes.shape({}),
  ignoreOnCloseRedirect: PropTypes.bool,
  initialScreen: PropTypes.string,
  isAppViewExcluded: PropTypes.func,
  isGoogleOneTapDisplayed: PropTypes.bool,
  isMobile: PropTypes.bool,
  isTablet: PropTypes.bool,
  language: PropTypes.shape({}),
  loader: PropTypes.node,
  login: PropTypes.func,
  logout: PropTypes.func,
  mvpdData: PropTypes.shape({}),
  network: PropTypes.arrayOf(PropTypes.shape({})),
  noVerifyAccount: PropTypes.func,
  onLoginClick: PropTypes.func,
  onSignupClick: PropTypes.func,
  onboardingStart: PropTypes.func,
  openAccount: PropTypes.func,
  openForgotPassword: PropTypes.func,
  pageViewEvent: PropTypes.func,
  optimizelyLanguageStrings: PropTypes.func,
  params: PropTypes.shape({}),
  perks: PropTypes.arrayOf(PropTypes.shape({})),
  perksPresented: PropTypes.func,
  previousPath: PropTypes.string,
  privacyPolicyClickEvent: PropTypes.shape({}),
  register: PropTypes.func,
  renderFavorites: PropTypes.func,
  renderNetworkLink: PropTypes.func,
  renderPerkImage: PropTypes.func,
  reset: PropTypes.func,
  resetAccountState: PropTypes.func,
  resetCode: PropTypes.string,
  resetConfirm: PropTypes.func,
  selectedNetwork: PropTypes.string,
  setAccountCloseIgnoreRedirect: PropTypes.func,
  setGoogleOneTap: PropTypes.func,
  showAccount: PropTypes.bool,
  showFacebookLogin: PropTypes.bool,
  showFacebookSignup: PropTypes.bool,
  showForgotPasswordLanding: PropTypes.bool,
  signInCompleted: PropTypes.func,
  signInError: PropTypes.func,
  signInForgotPasswordStarted: PropTypes.func,
  signInIncomplete: PropTypes.func,
  signInSuccess: PropTypes.func,
  signUpCompleted: PropTypes.func,
  signUpError: PropTypes.func,
  signUpIncomplete: PropTypes.func,
  signUpMarketing: PropTypes.func,
  signinDropdownIconTheme: PropTypes.shape({}),
  signupSuccess: PropTypes.func,
  socialFacebookLogin: PropTypes.func,
  socialFacebookRegister: PropTypes.func,
  toggleAccountExpired: PropTypes.func,
  toggleMvpdAuthOverlay: PropTypes.func,
  useMVPDLoginProfile: PropTypes.bool,
  utagAccountSource: PropTypes.string,
  verifyAccount: PropTypes.func,
};

const mapStateToProps = state => ({
  googleSignIn: state.app.googleSignIn,
  googleOneTap: state.app.googleOneTap,
  googleNewUser: state.app.googleNewUser,
  isGoogleOneTapDisplayed: state.app.isGoogleOneTapDisplayed,
});

const mapDispatchToProps = {
  setGoogleOneTap,
};

export default connect(mapStateToProps, mapDispatchToProps)(Account);
